import { createStore } from '@/common/pinia';
import type { ApiResponse } from '@/common/services/api.service-new';
import { ARR_POTENTIAL_DEFAULTS, HANSEN_YEARLY_DEFAULT_RANGE } from './hansen.const';
import type {
  ArrPotentialParams,
  ArrPotentialStatsResponse,
  ArrPotentialTileTemplateResponse,
  EudrLulcLossGroup,
  ForestLossGroup,
  HansenTreecover2000Response,
  HansenYearlyLayersResponse,
  TreecoverStatsResponse,
} from './hansen.types';

class HansenStore {
  public yearlyLayers: ApiResponse<HansenYearlyLayersResponse> | null = null;
  public treecover2000: ApiResponse<HansenTreecover2000Response> | null = null;
  public selectedYearRange: [number, number] = [...HANSEN_YEARLY_DEFAULT_RANGE];

  // ARR Potential state — tile-template URL (cached), current parameter set, and a
  // per-polygon-and-params stats cache. Param state lives in the store so the airborne
  // layer card and the OL tile composable share one source of truth across re-mounts.
  public arrPotentialTileTemplate: ApiResponse<ArrPotentialTileTemplateResponse> | null = null;
  public arrPotentialParams: ArrPotentialParams = { ...ARR_POTENTIAL_DEFAULTS };

  // Per-polygon stats cache. Keyed by JSON.stringify(MultiPolygonCoords); avoids
  // re-hitting /hansen/forestloss when only the year-range slider moved.
  private forestLossCache: Map<string, ForestLossGroup[]> = new Map();
  // EUDR loss-by-2020-LULC-class groups, keyed by JSON.stringify(MultiPolygonCoords).
  private lulcLossCache: Map<string, EudrLulcLossGroup[]> = new Map();
  private treecoverStatsCache: Map<string, TreecoverStatsResponse> = new Map();
  private arrPotentialStatsCache: Map<string, ArrPotentialStatsResponse> = new Map();

  public setYearlyLayers(yearlyLayers: ApiResponse<HansenYearlyLayersResponse> | null = null): void {
    this.yearlyLayers = yearlyLayers;
  }

  public setTreecover2000(treecover2000: ApiResponse<HansenTreecover2000Response> | null = null): void {
    this.treecover2000 = treecover2000;
  }

  public setSelectedYearRange(range: [number, number]): void {
    this.selectedYearRange = range;
  }

  public getForestLossCache(fingerprint: string): ForestLossGroup[] | undefined {
    return this.forestLossCache.get(fingerprint);
  }

  public setForestLossCache(fingerprint: string, groups: ForestLossGroup[]): void {
    this.forestLossCache.set(fingerprint, groups);
  }

  public getLulcLossCache(fingerprint: string): EudrLulcLossGroup[] | undefined {
    return this.lulcLossCache.get(fingerprint);
  }

  public setLulcLossCache(fingerprint: string, groups: EudrLulcLossGroup[]): void {
    this.lulcLossCache.set(fingerprint, groups);
  }

  public getTreecoverStatsCache(fingerprint: string): TreecoverStatsResponse | undefined {
    return this.treecoverStatsCache.get(fingerprint);
  }

  public setTreecoverStatsCache(fingerprint: string, stats: TreecoverStatsResponse): void {
    this.treecoverStatsCache.set(fingerprint, stats);
  }

  public setArrPotentialTileTemplate(
    template: ApiResponse<ArrPotentialTileTemplateResponse> | null = null,
  ): void {
    this.arrPotentialTileTemplate = template;
  }

  public setArrPotentialParams(params: ArrPotentialParams): void {
    this.arrPotentialParams = params;
  }

  public getArrPotentialStatsCache(key: string): ArrPotentialStatsResponse | undefined {
    return this.arrPotentialStatsCache.get(key);
  }

  public setArrPotentialStatsCache(key: string, stats: ArrPotentialStatsResponse): void {
    this.arrPotentialStatsCache.set(key, stats);
  }
}

const useHansenStore = createStore(HansenStore);

export { useHansenStore, type HansenStore };
